import { createTheme, alpha } from '@mui/material/styles';

const COLORS = {
  primary: '#7B6B8D',
  primaryDark: '#6A5A7A',
  primaryLight: '#9B8EAE',
  secondary: '#C4856A',
  secondaryDark: '#B0745A',
  bg: '#F9F7F4',
  surface: '#FFFFFF',
  dark: '#2A2535',
  border: '#E5DDD5',
  textMuted: '#8B7E75',
  textSecondary: '#5C5349',
};

const theme = createTheme({
  palette: {
    primary: {
      main: COLORS.primary,
      dark: COLORS.primaryDark,
      light: COLORS.primaryLight,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: COLORS.secondary,
      dark: COLORS.secondaryDark,
      contrastText: '#FFFFFF',
    },
    background: {
      default: COLORS.bg,
      paper: COLORS.surface,
    },
    text: {
      primary: COLORS.dark,
      secondary: COLORS.textSecondary,
      disabled: COLORS.textMuted,
    },
    divider: COLORS.border,
  },
  typography: {
    fontFamily: '"Jost", "Helvetica Neue", Arial, sans-serif',
    h1: { fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 },
    h2: { fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 },
    h3: { fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 },
    h4: { fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 500, letterSpacing: '0.025em', textTransform: 'none' },
  },
  shape: {
    borderRadius: 6,
  },
  shadows: [
    'none',
    '0 1px 3px rgba(42,37,53,0.06)',
    '0 2px 8px rgba(42,37,53,0.07)',
    '0 4px 16px rgba(42,37,53,0.08)',
    '0 6px 24px rgba(42,37,53,0.09)',
    '0 8px 32px rgba(42,37,53,0.10)',
    ...Array(19).fill('none'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        body {
          background-color: ${COLORS.bg};
        }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          boxShadow: 'none',
          textTransform: 'none',
          fontWeight: 500,
          letterSpacing: '0.025em',
          '&:hover': { boxShadow: 'none' },
          '&:active': { boxShadow: 'none' },
        },
        containedPrimary: {
          backgroundColor: COLORS.primary,
          '&:hover': { backgroundColor: COLORS.primaryDark },
        },
        containedSecondary: {
          backgroundColor: COLORS.secondary,
          '&:hover': { backgroundColor: COLORS.secondaryDark },
        },
        outlinedPrimary: {
          borderColor: COLORS.primary,
          color: COLORS.primary,
          '&:hover': {
            backgroundColor: alpha(COLORS.primary, 0.06),
            borderColor: COLORS.primaryDark,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            '&.Mui-focused fieldset': { borderColor: COLORS.primary },
          },
          '& .MuiInputLabel-root.Mui-focused': { color: COLORS.primary },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: COLORS.primary,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': { color: COLORS.primary },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.surface,
          color: COLORS.dark,
          boxShadow: 'none',
          borderBottom: `1px solid ${COLORS.border}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          boxShadow: '0 2px 12px rgba(42,37,53,0.06)',
          border: `1px solid ${COLORS.border}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        elevation1: { boxShadow: '0 2px 8px rgba(42,37,53,0.07)' },
        elevation2: { boxShadow: '0 4px 16px rgba(42,37,53,0.08)' },
        elevation3: { boxShadow: '0 6px 24px rgba(42,37,53,0.09)' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 4 },
        colorPrimary: {
          backgroundColor: alpha(COLORS.primary, 0.1),
          color: COLORS.primary,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { backgroundColor: COLORS.primary },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          '&.Mui-selected': { color: COLORS.primary },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: COLORS.primary },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: COLORS.primary,
            '& + .MuiSwitch-track': { backgroundColor: COLORS.primary },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': { color: COLORS.primary },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        bar: { backgroundColor: COLORS.primary },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&.Mui-selected': {
            backgroundColor: alpha(COLORS.primary, 0.1),
            color: COLORS.primary,
            '&:hover': { backgroundColor: alpha(COLORS.primary, 0.14) },
          },
          '&:hover': { backgroundColor: alpha(COLORS.primary, 0.06) },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: alpha(COLORS.primary, 0.06) },
          '&.Mui-selected': {
            backgroundColor: alpha(COLORS.primary, 0.1),
            '&:hover': { backgroundColor: alpha(COLORS.primary, 0.14) },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 12 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 6 },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          '& .MuiPickersDay-root.Mui-selected': {
            backgroundColor: COLORS.primary,
          },
        },
      },
    },
  },
});

export default theme;
export { COLORS };
